/**
 * QBlock — section card replica do `.q-block` do mockup 20:
 *   white bg, border-radius 18px, padding 32×36, border bege subtil.
 *
 * Usado nos 4 steps da calc-organização pra encapsular cada grupo de
 * perguntas (Equipe e público / Eletricidade / Gastos com deslocamento / etc).
 *
 * `title` e `subtitle` opcional ficam no topo do card.
 */
import type { ReactNode } from 'react';

interface QBlockProps {
  title?: string;
  subtitle?: string;
  children: ReactNode;
  /** Reduzir padding pra cards aninhados (ex.: bloco "audience" dentro de Deslocamento). */
  compact?: boolean;
}

export function QBlock({ title, subtitle, children, compact }: QBlockProps) {
  return (
    <section
      className={
        'bg-white border border-areia rounded-2xl transition-colors hover:border-cobre/40 ' +
        (compact ? 'p-5 md:p-6' : 'p-7 md:p-8')
      }
    >
      {title && (
        <header className="mb-5">
          <h3 className="font-display font-semibold text-xl text-floresta tracking-tight">
            {title}
          </h3>
          {subtitle && (
            <p className="font-body text-sm text-tabaco mt-1.5 leading-relaxed">{subtitle}</p>
          )}
        </header>
      )}
      {children}
    </section>
  );
}

/**
 * NavRow — barra inferior de "← Anterior · Passo N de M · Próximo →"
 * canônica do mockup 20 (`.nav-row` + `.btn-dark`/`.btn-ghost`).
 */
interface NavRowProps {
  onBack?: () => void;
  backLabel?: string;
  /** Desabilita o botão Anterior (ex.: durante submit/loading). */
  backDisabled?: boolean;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  /** Ex.: "Etapa 2 de 4" — vai no centro. */
  progress?: string;
  /** Quando true, o botão Próximo vira "submit" (form submit normal). */
  nextIsSubmit?: boolean;
}

export function NavRow({
  onBack,
  backLabel = '← Anterior',
  backDisabled,
  onNext,
  nextLabel = 'Próximo →',
  nextDisabled,
  progress,
  nextIsSubmit,
}: NavRowProps) {
  return (
    <div className="flex items-center justify-between gap-3 pt-6">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          disabled={backDisabled}
          className="font-display text-xs font-semibold uppercase tracking-[0.14em] px-7 py-3.5 rounded-2xl border border-floresta/40 text-floresta bg-transparent hover:border-cobre hover:text-cobre disabled:opacity-50 disabled:hover:border-floresta/40 disabled:hover:text-floresta transition-colors"
        >
          {backLabel}
        </button>
      ) : (
        <span />
      )}
      {progress && (
        <span className="font-mono text-xs text-tabaco">
          {progress}
        </span>
      )}
      <button
        type={nextIsSubmit ? 'submit' : 'button'}
        onClick={nextIsSubmit ? undefined : onNext}
        disabled={nextDisabled}
        className="font-display text-xs font-semibold uppercase tracking-[0.14em] px-7 py-3.5 rounded-2xl bg-floresta text-cream hover:bg-cobre disabled:opacity-50 disabled:hover:bg-floresta shadow-[0_6px_18px_rgba(20,28,38,0.12)] transition-colors"
      >
        {nextLabel}
      </button>
    </div>
  );
}
